import { useCallback } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { setConceptGroupSelections } from '@verantos-dev/app-state-cohort-builder';
import type {
  AnalyticalVariableWithChangeStatus,
  StudyPeriodState,
} from '@verantos-dev/app-state-cohort-definition';
import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';
import {
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
} from '@verantos-dev/types-cohorts';
import type { ConceptGroupSelection, ConceptGroupsConfidence } from '@verantos-dev/types-concepts';

export const useResearchAssistantCohortCallbacks = () => {
  const dispatch = useAppDispatch();
  const onCohortDslReceived = useCallback((definition: CohortBuilderDefinitionLegacy) => {
    const store = useCohortDefinitionStore.getState();
    const studyPeriod = definition.studyPeriodRestrictions;
    store.setFromServer(
      definition.analyticalVariables ?? [],
      definition.criteria ?? [],
      {
        overallStudyPeriodRestriction: studyPeriod?.overallStudyPeriodRestriction,
        changeStatus: studyPeriod?.changeStatus,
        changedBy: studyPeriod?.changedBy,
      },
      {
        simpleEvents: definition.simpleEvents ?? [],
        derivedEvents: definition.derivedEvents ?? [],
        indexDate: definition.indexDate,
        subjectVariables: definition.subjectVariables ?? [],
      }
    );
    // Prevent TQ background re-fetches from overwriting with pre-RA data before cohort_saved.
    store.setHasStagedChanges(true);
  }, []);

  const onPartialCohortDslReceived = useCallback((dsl: Record<string, unknown>) => {
    try {
      const avs = ((dsl.analyticalVariables as Record<string, unknown>[]) ?? []).map((av) => ({
        ...av,
        changeStatus: CohortBuilderItemChangeStatusLegacy.NEW,
        changedBy: CohortBuilderItemChangedBy.RESEARCH_ASSISTANT,
      })) as AnalyticalVariableWithChangeStatus[];
      const store = useCohortDefinitionStore.getState();
      const criteria = (dsl.criteria ?? []) as Parameters<typeof store.setFromServer>[1];
      const spr = (dsl.studyPeriodRestrictions ?? {}) as StudyPeriodState;
      store.setFromServer(avs, criteria, spr);
    } catch (_) {
      // partial DSL may be incomplete during streaming — ignore parse errors
    }
  }, []);

  const onConceptGroupConfidence = useCallback((confidence: ConceptGroupsConfidence) => {
    useCohortDefinitionStore.getState().setConceptGroupsConfidence(confidence);
  }, []);

  const onClearConceptGroupConfidence = useCallback(() => {
    useCohortDefinitionStore.getState().clearConceptGroupsConfidence();
  }, []);

  const onMarkConceptGroupAsGenerated = useCallback((conceptGroupName: string) => {
    useCohortDefinitionStore.getState().markConceptGroupAsGenerated(conceptGroupName);
  }, []);

  const onConceptGroupSelections = useCallback(
    (selections: ConceptGroupSelection[]) => {
      useCohortDefinitionStore.getState().setConceptGroupSelections(selections);
      // Dual-write to Redux until feature-research-assistant (useConceptGroupSuggestedActions)
      // is migrated to read from Zustand — tracked in PR5
      dispatch(setConceptGroupSelections({ conceptGroupSelections: selections }));
    },
    [dispatch]
  );

  return {
    onCohortDslReceived,
    onPartialCohortDslReceived,
    onConceptGroupConfidence,
    onClearConceptGroupConfidence,
    onMarkConceptGroupAsGenerated,
    onConceptGroupSelections,
  };
};
